import { useTemplate } from "@hooks/useTemplate";
import { useTrendii } from "@hooks/useTrendii";
import { ProductVariant } from "@shopify/hydrogen-react/storefront-api-types";
import { ComponentProps, NormalisedProductVariant } from "@ts/components";
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useState,
} from "react";

import { useCartAdd } from "~/hooks/useCartNew";

export type AddToCartInputProps = ComponentProps & {
  cart?: boolean;
  notify?: boolean;
  selectedQuantity?: number;
  selectedVariant?: NormalisedProductVariant | ProductVariant;
  selectedVariantAccessories?: NormalisedProductVariant[];
  wishlist?: boolean;
  upsell?: boolean;
};

export type AddToCartOutputProps = Omit<
  AddToCartInputProps,
  "selectedQuantity"
> & {
  active: boolean;
  additionalAddingToCart?: string;
  additionalAddToCart?: string;
  additionalNotify?: string;
  additionalOutOfStock?: string;
  className?: string;
  errorActive: boolean;
  loading: boolean;
  onAddToCart: (id: string) => void;
  productUpsellActive: boolean;
  setActive: Dispatch<SetStateAction<boolean>>;
  setErrorActive: Dispatch<SetStateAction<boolean>>;
  setProductUpsellActive: Dispatch<SetStateAction<boolean>>;
};

export const withAddToCart =
  (Component: FC<AddToCartOutputProps>) =>
  ({
    name = "AddToCart",
    selectedVariant,
    selectedVariantAccessories,
    notify,
    wishlist,
    cart,
    selectedQuantity = 1,
    upsell,
    ...rest
  }: AddToCartInputProps) => {
    const { addToCart, isPending } = useCartAdd();

    const {
      product: {
        additionalAddingToCart,
        additionalAddToCart,
        additionalOutOfStock,
        additionalNotify,
      },
    } = useTemplate();

    const trendii = useTrendii();

    const [backToStockActive, setBackToStockActive] = useState(false);
    const [errorActive, setErrorActive] = useState(false);
    const [productUpsellActive, setProductUpsellActive] = useState(false);

    const handleAddToCart = useCallback(
      (merchandiseId: string) => {
        if (!selectedVariant) return;

        addToCart({ merchandiseId, quantity: selectedQuantity });

        trendii.trackAddToCart(
          {
            id: selectedVariant.id,
            title: selectedVariant.title,
            sku: selectedVariant.sku,
            price: selectedVariant.price.amount,
            currency: selectedVariant.price.currencyCode,
          },
          selectedQuantity,
        );
      },
      [selectedVariant, addToCart, selectedQuantity],
    );

    Component.displayName = name;

    return (
      <Component
        {...rest}
        active={backToStockActive}
        additionalAddingToCart={additionalAddingToCart}
        additionalAddToCart={additionalAddToCart}
        additionalNotify={additionalNotify}
        additionalOutOfStock={additionalOutOfStock}
        cart={cart}
        errorActive={errorActive}
        loading={isPending}
        notify={notify}
        onAddToCart={handleAddToCart}
        productUpsellActive={productUpsellActive}
        selectedVariant={selectedVariant}
        setActive={setBackToStockActive}
        setErrorActive={setErrorActive}
        setProductUpsellActive={setProductUpsellActive}
        wishlist={wishlist}
        upsell={upsell}
      />
    );
  };
